import React from "react";

/**
 * Configuration component for MentionInput mention types.
 *
 * This is a declarative component (renders null) used to configure
 * mention/command triggers within a MentionInput. Each <Mention> child
 * defines a trigger character, data source, and custom rendering.
 *
 * Inspired by react-mentions' Mention component pattern.
 *
 * @example
 * <MentionInput value={value} onChange={onChange}>
 *   <Mention
 *     trigger="/"
 *     type="skill"
 *     data={skills}
 *     renderSuggestions={(props) => <SkillsPopup {...props} />}
 *   />
 *   <Mention
 *     trigger="@"
 *     type="user"
 *     data={users}
 *     renderSuggestions={(props) => <UserList {...props} />}
 *   />
 * </MentionInput>
 */

export interface SuggestionRenderProps {
  /** Filtered suggestions based on search text */
  suggestions: any[];

  /** Current search text after trigger (e.g., "ski" in "/ski") */
  searchText: string;

  /** Position to render suggestions (fixed positioning) */
  position: { top: number; left: number };

  /** Callback to insert selected value at cursor */
  onSelect: (value: string) => void;

  /** Callback to close suggestions */
  onClose: () => void;
}

export interface MentionProps {
  /**
   * Trigger character(s) that activate this mention type.
   * Examples: "/", "@", "#"
   */
  trigger: string;

  /**
   * Type identifier for this mention (used in mention data).
   * Examples: "skill", "user", "tag"
   */
  type: string;

  /**
   * Regex pattern to match committed blocks in text.
   * If omitted, auto-derived from trigger.
   * Example: /\/[\w-]+/g for trigger="/"
   */
  pattern?: RegExp;

  /**
   * CSS className for styling committed blocks.
   * Example: "bg-gray-200 text-gray-800 rounded px-1.5 py-0.5"
   */
  className?: string;

  /**
   * Data source for suggestions. Can be:
   * - Array: Simple client-side filtering
   * - Function: Async queries, complex filtering logic
   *
   * Function signature:
   * (query: string, callback: (results: any[]) => void) => void | any[]
   *
   * If function returns array synchronously, it's used immediately.
   * Otherwise, callback should be called with results.
   */
  data?: any[] | ((query: string, callback: (results: any[]) => void) => void | any[]);

  /**
   * Transform function to extract display string from data item.
   * Default: (item) => item.display || item.name || item.id
   */
  displayTransform?: (item: any) => string;

  /**
   * Whether to append space after inserting mention.
   * Default: true
   */
  appendSpaceOnAdd?: boolean;

  /**
   * Custom suggestion UI renderer.
   * Receives filtered suggestions and callbacks.
   * Full control over rendering, positioning, and interaction.
   *
   * REQUIRED - no default implementation.
   */
  renderSuggestions: (props: SuggestionRenderProps) => React.ReactNode;
}

/**
 * Mention component - configuration only, no rendering.
 *
 * This component is read by MentionInput via React.Children
 * to extract mention configuration. It never renders anything itself.
 */
export const Mention: React.FC<MentionProps> = () => null;

Mention.displayName = "Mention";
